/**
 * Utility for mobile haptic feedback / vibration.
 * Supports:
 * - Standard Vibration API (navigator.vibrate) for Android and compatible mobile browsers.
 * - iOS Safari 18+ fallback using native switch control haptics.
 */
import { useEffect } from "react";

// Cached switch element for iOS Safari Taptic Engine simulation
let iosSwitchEl = null;
let lastVibratedAt = 0;
const VIBRATION_THROTTLE_MS = 50;

/**
 * Determine if the current environment is a mobile / touch device
 */
export const isMobileDevice = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) ||
    /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(
      navigator.userAgent
    )
  );
};

/**
 * iOS 18+ Safari Taptic Engine trigger using the native switch control
 */
const triggerIOSHaptic = () => {
  if (typeof document === "undefined" || !document.body) return;
  try {
    if (!iosSwitchEl) {
      iosSwitchEl = document.createElement("input");
      iosSwitchEl.type = "checkbox";
      iosSwitchEl.setAttribute("switch", "");
      iosSwitchEl.setAttribute("aria-hidden", "true");
      iosSwitchEl.tabIndex = -1;
      iosSwitchEl.style.position = "fixed";
      iosSwitchEl.style.top = "-9999px";
      iosSwitchEl.style.left = "-9999px";
      iosSwitchEl.style.opacity = "0";
      iosSwitchEl.style.pointerEvents = "none";
      iosSwitchEl.style.width = "0";
      iosSwitchEl.style.height = "0";
      document.body.appendChild(iosSwitchEl);
    }
    iosSwitchEl.click();
  } catch (err) {
    // Graceful fallback if not supported
  }
};

/**
 * Trigger mobile vibration / haptic feedback
 * @param {number|number[]} [pattern=15] - Duration in ms or pattern
 */
export const vibrate = (pattern = 15) => {
  if (typeof window === "undefined") return;

  const now = Date.now();
  if (now - lastVibratedAt < VIBRATION_THROTTLE_MS) {
    return; // Prevent duplicate rapid vibrations
  }
  lastVibratedAt = now;

  // Only trigger on mobile devices
  if (!isMobileDevice()) {
    return;
  }

  try {
    // 1. Standard Vibration API (Android Chrome, Firefox, Samsung Internet, etc.)
    if (
      typeof navigator !== "undefined" &&
      "vibrate" in navigator &&
      typeof navigator.vibrate === "function"
    ) {
      const vibrated = navigator.vibrate(pattern);
      if (vibrated) return;
    }

    // 2. iOS 18+ Safari fallback
    triggerIOSHaptic();
  } catch (err) {
    // Gracefully handle any browser permission restriction
  }
};

/**
 * Global hook to enable vibration feedback for interactive elements (buttons, links, images, etc.)
 * on mobile browsers.
 */
export const useMobileVibration = () => {
  useEffect(() => {
    if (typeof window === "undefined" || !isMobileDevice()) return;

    const interactiveSelectors = [
      "button",
      "a",
      "img",
      '[role="button"]',
      'input[type="button"]',
      'input[type="submit"]',
      'input[type="reset"]',
      "summary",
      "[data-vibrate]",
    ].join(", ");

    const handlePointerOrClick = (e) => {
      const target = e.target;
      if (!target || !(target instanceof Element)) return;

      // Check if target or an ancestor is a button, link, image, or role="button"
      const matchedInteractive = target.closest(interactiveSelectors);
      if (matchedInteractive) {
        vibrate(15);
        return;
      }

      // Check if element or parent has cursor: pointer (covers custom interactive elements)
      let current = target;
      let depth = 0;
      while (current && current !== document.body && depth < 5) {
        try {
          const style = window.getComputedStyle(current);
          if (style && style.cursor === "pointer") {
            vibrate(15);
            return;
          }
        } catch (styleErr) {
          break;
        }
        current = current.parentElement;
        depth++;
      }
    };

    // Capture phase ensures we receive the event even if stopPropagation is used
    document.addEventListener("click", handlePointerOrClick, {
      capture: true,
      passive: true,
    });

    return () => {
      document.removeEventListener("click", handlePointerOrClick, {
        capture: true,
      });
    };
  }, []);
};
